import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';

export function AdminSystemPromptsList() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'system-prompts'],
    queryFn: api.adminSystemPromptList,
  });

  const prompts = data?.prompts ?? [];

  return (
    <PageShell
      header={
        <PageHeader
          title="시스템 프롬프트"
          count={prompts.length}
          description="LLM 호출에 사용되는 시스템 프롬프트 — 기본값은 코드에 박혀있고 여기서 덮어쓸 수 있습니다. 빈 값으로 저장하면 해당 가이드가 비활성화됩니다."
        />
      }
    >
      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : '목록을 불러오지 못했습니다.'}
        </p>
      ) : (
        <div className="rounded-2xl border border-border bg-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
              <tr>
                <Th>변수</Th>
                <Th>설명</Th>
                <Th>상태</Th>
                <Th>업데이트</Th>
                <Th align="right">액션</Th>
              </tr>
            </thead>
            <tbody>
              {prompts.map((p) => (
                <tr key={p.key} className="border-t border-border hover:bg-surface-muted/30">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() =>
                        navigate({ to: '/admin/system-prompts/$key', params: { key: p.key } })
                      }
                      className="text-left hover:underline"
                    >
                      <div className="font-medium">{p.label}</div>
                      <div className="mt-0.5 font-mono text-[11px] text-foreground-muted">
                        {p.key}
                      </div>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-foreground-muted">{p.description}</td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider',
                        p.isDefault
                          ? 'bg-surface-muted text-foreground-muted'
                          : 'bg-success/15 text-success',
                      )}
                    >
                      {p.isDefault ? '기본값' : '변경됨'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-foreground-muted text-xs">
                    {p.updatedAt ? formatRelative(p.updatedAt) : '—'}
                    {p.updatedBy ? (
                      <span className="ml-1.5">
                        ({p.updatedBy.displayName ?? p.updatedBy.email ?? '?'})
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      aria-label="편집"
                      onClick={() =>
                        navigate({
                          to: '/admin/system-prompts/$key/edit',
                          params: { key: p.key },
                        })
                      }
                      className="size-8 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
                    >
                      <Pencil className="size-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {prompts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-foreground-muted py-10">
                    등록된 시스템 프롬프트가 없습니다.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-2.5 font-medium', align === 'right' ? 'text-right' : 'text-left')}
    >
      {children}
    </th>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = now - then;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec}초 전`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}일 전`;
  return new Date(iso).toLocaleDateString('ko-KR');
}
